import type { MediaEntityInfo } from "@/types/api";

const MS_48H = 48 * 60 * 60 * 1000;

/** Allowed `video_model` values for POST extension (entity lambda / readme). */
export const VEO_EXTEND_MODEL = {
  V31: "veo-3.1-generate-preview",
  V31_FAST: "veo-3.1-fast-generate-preview",
  V30: "veo-3.0-generate-001",
  V30_FAST: "veo-3.0-fast-generate-001"
} as const;

export const EXTEND_PROMPT_MAX_LEN = 2000;
export const EXTENSION_DURATION_SEC = 7;
export const TRANSACTION_EXTEND_POLL_MS = 5000;

export const readProviderId = (info: MediaEntityInfo | undefined): string => {
  if (!info) {
    return "";
  }

  const snake = info.provider_id;
  if (typeof snake === "string" && snake.trim().length > 0) {
    return snake.trim();
  }

  const camel = info.providerId;
  if (typeof camel === "string" && camel.trim().length > 0) {
    return camel.trim();
  }

  return "";
};

/** Veo 3.x video model ids from GET /metadata (`video_models[].video_model`). */
export const isVeo3DotXVideoModel = (videoModel: string | undefined): boolean => {
  const s = (videoModel ?? "").trim().toLowerCase();
  if (s.startsWith("veo-3.")) {
    return true;
  }

  return s === "veo-3.1-generate-preview_alias";
};

/** Label for the duration dropdown; Veo 3.x maps 4s/6s to 720p and 8s to 1080p. */
export const durationOptionLabel = (seconds: number, videoModel: string | undefined): string => {
  if (!isVeo3DotXVideoModel(videoModel)) {
    return `${seconds}s`;
  }

  if (seconds === 8) {
    return `${seconds}s (1080p)`;
  }

  if (seconds === 4 || seconds === 6) {
    return `${seconds}s (720p)`;
  }

  return `${seconds}s`;
};

export const readVideoDurationSeconds = (info: MediaEntityInfo | undefined): number | null => {
  const raw = info?.duration;
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) {
    return null;
  }

  return Math.round(raw);
};

/** Veo extension requires an 8s source clip (1080p). */
export const isExtendEligibleDuration = (info: MediaEntityInfo | undefined): boolean => {
  const duration = readVideoDurationSeconds(info);
  return duration === null || duration === 8;
};

/** Veo 3.x: Google model ids (`veo-3.…`) or Runway enum values stored on `VideoInfo.source` (`runway_ml_veo_3.0`, …). */
export const isVeo3DotXSource = (source: string | undefined): boolean => {
  const s = (source ?? "").trim().toLowerCase();
  if (s.startsWith("veo-3.")) {
    return true;
  }

  return /^runway_ml_veo_3\.\d/.test(s);
};

const OMNI_VIDEO_MODEL = "gemini-omni-flash-preview";
const OMNI_EDIT_VIDEO_MODEL = "gemini-omni-flash-edit-preview";

export const isOmniVideoModel = (videoModel: string | undefined): boolean =>
  (videoModel ?? "").trim().toLowerCase() === OMNI_VIDEO_MODEL;

export const isOmniEditVideoModel = (videoModel: string | undefined): boolean =>
  (videoModel ?? "").trim().toLowerCase() === OMNI_EDIT_VIDEO_MODEL;

export const isOmniFamilyVideoModel = (videoModel: string | undefined): boolean =>
  isOmniVideoModel(videoModel) || isOmniEditVideoModel(videoModel);

export const isOmniSource = (source: string | undefined): boolean => isOmniFamilyVideoModel(source);

export const parseCreatedDate = (raw: string | undefined): Date | null => {
  if (!raw?.trim()) {
    return null;
  }

  const d = new Date(raw.trim());
  return Number.isNaN(d.getTime()) ? null : d;
};

export const isCreatedWithinLast48Hours = (created: Date | null, now: Date = new Date()): boolean => {
  if (!created) {
    return false;
  }

  const ageMs = now.getTime() - created.getTime();
  return ageMs >= 0 && ageMs <= MS_48H;
};

export const buildCannotExtendReasons = (info: MediaEntityInfo | undefined): string[] => {
  const reasons: string[] = [];
  const source = info?.source;

  if (!isVeo3DotXSource(source)) {
    reasons.push("This clip was not generated with a supported Veo 3 model, so the extension service cannot continue it.");
  }

  if (!readProviderId(info)) {
    reasons.push("The original provider video ID is missing, so we cannot send the source clip back for extension.");
  }

  const created = parseCreatedDate(info?.created_date);
  if (!created) {
    reasons.push("The creation date is missing, so we cannot confirm it is still eligible for extension.");
  } else if (!isCreatedWithinLast48Hours(created)) {
    reasons.push("The clip is older than 48 hours, and the provider only allows extension within that window.");
  }

  if (!isExtendEligibleDuration(info)) {
    reasons.push("Extension is only available for 8 second (1080p) clips.");
  }

  return reasons;
};

export const canExtendVideo = (info: MediaEntityInfo | undefined): boolean => {
  if (!info) {
    return false;
  }

  return (
    isVeo3DotXSource(info.source) &&
    readProviderId(info).length > 0 &&
    isCreatedWithinLast48Hours(parseCreatedDate(info.created_date)) &&
    isExtendEligibleDuration(info)
  );
};

/** Maps stored `VideoInfo.source` to an API `video_model` for extension POST. */
export const extensionVideoModelForSource = (source: string | undefined): string => {
  const s = (source ?? "").trim();
  const lower = s.toLowerCase();
  const allowed = new Set<string>(Object.values(VEO_EXTEND_MODEL));
  if (allowed.has(s)) {
    return s;
  }

  if (lower === "runway_ml_veo_3.1") {
    return VEO_EXTEND_MODEL.V31;
  }

  if (lower === "runway_ml_veo_3.0") {
    return VEO_EXTEND_MODEL.V30;
  }

  if (lower.startsWith("veo-3.1") && lower.includes("fast")) {
    return VEO_EXTEND_MODEL.V31_FAST;
  }

  if (lower.startsWith("veo-3.1")) {
    return VEO_EXTEND_MODEL.V31;
  }

  if (lower.startsWith("veo-3.0") && lower.includes("fast")) {
    return VEO_EXTEND_MODEL.V30_FAST;
  }

  if (lower.startsWith("veo-3.0")) {
    return VEO_EXTEND_MODEL.V30;
  }

  return VEO_EXTEND_MODEL.V31;
};
